import DatePicker, { DatePickerProps } from "react-datepicker";
import { BaseInputProps, ErrorMessage } from ".";
import { Controller, useFormContext } from "react-hook-form";
import { usePermissions } from "../../../contexts";
import { useMemo } from "react";
import { Form } from "react-bootstrap";
import { PatternFormat } from "react-number-format";
import {
    getDisplayedAccessor,
    getFormError,
    getTimeZonelessDisplayDate,
    handleChangeTimeZonelessDate,
} from "../../../helper-functions";

export type DateProps = BaseInputProps &
    DatePickerProps & {
        "label"?: string;
        "inlineLabel"?: boolean;
        "withTime"?: boolean;
        "onlyTime"?: boolean;
        "inGroup"?: boolean;
        "aria-label"?: string;
        "ignoreTimeZone"?: boolean;
    };

export function DateInput({
    accessor,
    label,
    inlineLabel,
    viewClaim,
    editClaim,
    readOnly,
    className,
    suppressMargin,
    withTime,
    onlyTime,
    dateFormat,
    timeFormat,
    inGroup,
    title,
    "aria-label": ariaLabel,
    ignoreTimeZone,
    isClearable,
    ...rest
}: Readonly<DateProps>) {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const { hasPermission } = usePermissions();
    const error = useMemo(() => getFormError(errors, accessor), [errors, accessor]);

    const isReadOnly = useMemo(
        () => (editClaim ? !hasPermission(editClaim) : readOnly),
        [readOnly, editClaim, hasPermission]
    );

    const format = useMemo<string | string[] | undefined>(() => {
        if (dateFormat) return dateFormat;
        if (withTime) return "MM/dd/yyyy, HH:mm";
        if (onlyTime) return "HH:mm";
        return "MM/dd/yyyy";
    }, [dateFormat, onlyTime, withTime]);

    const mask = useMemo(() => {
        if (format === "MM/dd/yyyy") return "##/##/####";
        if (format === "HH:mm") return "##:##";
        if (format === "MM/dd/yyyy, HH:mm") return "##/##/####, ##:##";
        return undefined;
    }, [format]);

    const wrapperClassName = useMemo(() => {
        if (onlyTime) return "datepicker-time-wrapper";
        return withTime ? "" : "datepicker-date-wrapper";
    }, [onlyTime, withTime]);

    const getSelected = (value: any) => {
        if (ignoreTimeZone) return getTimeZonelessDisplayDate(value);
        return value ? new Date(value) : undefined;
    };

    const handleChange = (args: any[], onChange: any) => {
        const value = args[0]?.toISOString();
        if (ignoreTimeZone) {
            onChange(handleChangeTimeZonelessDate(value));
        } else {
            onChange(value || null);
        }
    };

    const customInput = useMemo(() => {
        if (!mask) return undefined;

        return <PatternFormat format={mask} mask="_" className="form-control" />;
    }, [mask]);

    if (!!viewClaim && !hasPermission(viewClaim)) return null;

    if (!inGroup)
        return (
            <Form.Group controlId={accessor} className={`${!suppressMargin && "mb-2"} ${className}`}>
                <div className={inlineLabel ? "d-flex align-items-center" : ""}>
                    {!!label && (
                        <div>
                            <Form.Label style={{ whiteSpace: "nowrap" }} className={inlineLabel ? "me-2" : ""}>
                                {label} {rest.required && <span className="text-danger">*</span>}
                            </Form.Label>
                        </div>
                    )}
                    <Controller
                        control={control}
                        name={accessor}
                        render={({ field: { value, onChange, ...otherFields } }) => {
                            return (
                                <div className="d-flex align-items-center">
                                    <DatePicker
                                        selected={getSelected(value)}
                                        onChange={(...args: any[]) => handleChange(args, onChange)}
                                        readOnly={isReadOnly}
                                        aria-readonly={isReadOnly}
                                        aria-label={ariaLabel ?? label ?? accessor}
                                        showTimeSelect={withTime ?? onlyTime}
                                        showTimeSelectOnly={onlyTime}
                                        dateFormat={format}
                                        timeFormat={(timeFormat ?? (withTime || onlyTime)) ? "HH:mm" : undefined}
                                        title={title ?? getDisplayedAccessor(accessor)}
                                        isClearable={isClearable ?? !isReadOnly}
                                        {...otherFields}
                                        {...rest}
                                        className="form-control"
                                        timeIntervals={1}
                                        wrapperClassName={wrapperClassName}
                                        customInput={customInput}
                                    />
                                </div>
                            );
                        }}
                    />
                </div>
                <ErrorMessage error={error} />
            </Form.Group>
        );

    return (
        <>
            <Controller
                control={control}
                name={accessor}
                render={({ field: { value, onChange, ...otherFields } }) => (
                    <div className="d-flex align-items-center">
                        <DatePicker
                            selected={getSelected(value)}
                            onChange={(...args: any[]) => handleChange(args, onChange)}
                            readOnly={isReadOnly}
                            aria-readonly={isReadOnly}
                            aria-label={ariaLabel ?? label ?? accessor}
                            showTimeSelect={withTime ?? onlyTime}
                            showTimeSelectOnly={onlyTime}
                            dateFormat={format}
                            timeFormat={(timeFormat ?? (withTime || onlyTime)) ? "HH:mm" : undefined}
                            title={title ?? getDisplayedAccessor(accessor)}
                            isClearable={isClearable ?? !isReadOnly}
                            {...otherFields}
                            {...rest}
                            className={`${className} form-control`}
                            timeIntervals={1}
                            wrapperClassName={wrapperClassName}
                            customInput={customInput}
                        />
                    </div>
                )}
            />
            <ErrorMessage error={error} />
        </>
    );
}
